import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft, ChevronRight, Briefcase, Upload, X, Check, Shield, FileText,
    Plus, Lightbulb, Award, Building2, FlaskConical, Target,
    BookOpen, ClipboardList, GraduationCap, User, Headphones
} from 'lucide-react';

const ProfessionDocuments = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const [uploadedDocs, setUploadedDocs] = useState({});
    const [customDocuments, setCustomDocuments] = useState({});
    const [showAddCustom, setShowAddCustom] = useState(false);
    const [newCustomDocName, setNewCustomDocName] = useState('');
    const [currentStep, setCurrentStep] = useState(1);

    // Get user's selected professions from localStorage
    const [userProfessions, setUserProfessions] = useState([]);

    useEffect(() => {
        const storedProfile = localStorage.getItem('userProfile');
        if (storedProfile) {
            try {
                const profile = JSON.parse(storedProfile);
                if (profile.profession && Array.isArray(profile.profession)) {
                    setUserProfessions(profile.profession);
                }
            } catch (e) {
                console.error('Error parsing profile:', e);
            }
        }
    }, []);

    // Document configurations for each profession
    const professionDocuments = {
        'Student/Exam Aspirant': {
            icon: GraduationCap,
            color: 'blue',
            documents: [
                { id: 'student_id', label: 'Student ID Card', icon: User },
                { id: 'admit_card', label: 'Admit Card', icon: FileText },
                { id: 'enrollment_letter', label: 'Enrollment Letter', icon: FileText },
                { id: 'fee_receipt', label: 'Fee Receipt', icon: FileText }
            ]
        },
        'Working Professional': {
            icon: Briefcase,
            color: 'purple',
            documents: [
                { id: 'offer_letter', label: 'Offer Letter', icon: FileText },
                { id: 'experience_letter', label: 'Experience Letter', icon: Award },
                { id: 'salary_slip', label: 'Salary Slip', icon: FileText },
                { id: 'employee_id', label: 'Employee ID', icon: User }
            ]
        },
        'Founder': {
            icon: Lightbulb,
            color: 'orange',
            documents: [
                { id: 'pitchdeck', label: 'Pitchdeck', icon: FileText },
                { id: 'dpiit_certificate', label: 'DPIIT Certificate', icon: Award },
                { id: 'incorporation_certificate', label: 'Incorporation Certificate', icon: Building2 }
            ]
        },
        'Researcher': {
            icon: FlaskConical,
            color: 'green',
            documents: [
                { id: 'research_proposal', label: 'Research Proposal (Detailed / PDF)', icon: BookOpen },
                { id: 'research_abstract', label: 'Research Abstract / Executive Summary', icon: FileText },
                { id: 'research_objectives', label: 'Research Objectives & Problem Statement', icon: Target },
                { id: 'research_methodology', label: 'Research Methodology / Approach', icon: ClipboardList }
            ]
        },
        'Service Center Operator': {
            icon: Headphones,
            color: 'cyan',
            documents: [
                { id: 'service_license', label: 'Service License', icon: Award },
                { id: 'business_registration', label: 'Business Registration', icon: Building2 },
                { id: 'gst_certificate', label: 'GST Certificate', icon: FileText },
                { id: 'trade_license', label: 'Trade License', icon: FileText }
            ]
        },
        'Other': {
            icon: User,
            color: 'gray',
            documents: [
                { id: 'other_doc1', label: 'Professional Document 1', icon: FileText },
                { id: 'other_doc2', label: 'Professional Document 2', icon: FileText }
            ]
        }
    };

    const getColorClasses = (color) => {
        const colors = {
            blue: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600' },
            purple: { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600' },
            orange: { bg: 'bg-orange-500', light: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600' },
            green: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-200', text: 'text-green-600' },
            cyan: { bg: 'bg-cyan-500', light: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-600' },
            gray: { bg: 'bg-gray-500', light: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600' }
        };
        return colors[color] || colors.gray;
    };

    // Filter active professions
    const activeProfessions = userProfessions.filter(p => professionDocuments[p]);
    const totalPages = activeProfessions.length;
    const currentProfessionName = activeProfessions[currentStep - 1];
    const currentProfession = currentProfessionName ? professionDocuments[currentProfessionName] : null;

    const handleFileUpload = (fieldId, event) => {
        const file = event.target.files[0];
        if (file) {
            const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
            setUploadedDocs(prev => ({
                ...prev,
                [fieldId]: {
                    file: file,
                    name: file.name,
                    preview: previewUrl,
                    size: (file.size / 1024).toFixed(1) + ' KB'
                }
            }));
        }
        setActiveField(null);
    };

    const triggerFileUpload = (fieldId) => {
        setActiveField(fieldId);
        fileInputRef.current?.click();
    };

    const removeDocument = (fieldId) => {
        setUploadedDocs(prev => {
            const updated = { ...prev };
            if (updated[fieldId]?.preview) {
                URL.revokeObjectURL(updated[fieldId].preview);
            }
            delete updated[fieldId];
            return updated;
        });
    };

    const addCustomDocument = () => {
        if (!newCustomDocName.trim() || !currentProfessionName) return;
        const customDoc = {
            id: `custom_${Date.now()}`,
            label: newCustomDocName.trim(),
            icon: FileText,
            isCustom: true
        };
        setCustomDocuments(prev => ({
            ...prev,
            [currentProfessionName]: [...(prev[currentProfessionName] || []), customDoc]
        }));
        setNewCustomDocName('');
        setShowAddCustom(false);
    };

    const removeCustomDocument = (docId) => {
        setCustomDocuments(prev => ({
            ...prev,
            [currentProfessionName]: (prev[currentProfessionName] || []).filter(d => d.id !== docId)
        }));
        removeDocument(docId);
    };

    const handleSave = () => {
        console.log('Saving profession documents:', uploadedDocs);
        navigate('/docs');
    };

    // Progress calculation
    const getProgress = () => {
        const allDocs = activeProfessions.flatMap(p =>
            professionDocuments[p]?.documents.map(d => d.id) || []
        );
        const uploadedCount = allDocs.filter(id => uploadedDocs[id]).length;
        return allDocs.length > 0 ? Math.round((uploadedCount / allDocs.length) * 100) : 0;
    };

    if (activeProfessions.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-4 mb-6">
                        <button onClick={() => navigate('/docs')} className="p-2 hover:bg-gray-100 rounded-xl">
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">Professional Documents</h1>
                    </div>
                    <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Briefcase className="w-10 h-10 text-gray-300" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-700 mb-2">No Profession Selected</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            Please complete your profile and select your profession(s) to see relevant documents.
                        </p>
                        <button
                            onClick={() => navigate('/signup-page2')}
                            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                        >
                            Complete Profile
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const colorClasses = currentProfession ? getColorClasses(currentProfession.color) : getColorClasses('gray');
    const ProfessionIcon = currentProfession?.icon || Briefcase;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
            <div className="max-w-3xl mx-auto">

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => activeField && handleFileUpload(activeField, e)}
                />

                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Professional Documents</h1>
                </div>

                {/* Step Indicators - Like Signup Pages */}
                <div className="flex gap-4 mb-6 px-2">
                    {activeProfessions.map((profName, index) => {
                        const stepNum = index + 1;
                        const isActive = currentStep === stepNum;
                        const isPast = currentStep > stepNum;
                        const prof = professionDocuments[profName];
                        const StepIcon = prof?.icon || Briefcase;

                        return (
                            <div key={profName} className="flex flex-col gap-1.5 flex-1">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-500 ${isPast ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                                            : isActive ? 'bg-blue-400'
                                                : 'bg-gray-200'
                                        }`}
                                />
                                <div className="flex items-center gap-1.5">
                                    <StepIcon className={`w-3 h-3 ${isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-400'
                                        }`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-blue-600' : isPast ? 'text-green-600' : 'text-gray-400'
                                        }`}>
                                        {profName}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-6 border border-white/60 backdrop-blur-sm">
                    {/* Page Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 ${colorClasses.light} rounded-xl`}>
                                <ProfessionIcon className={`w-4 h-4 ${colorClasses.text}`} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{currentProfessionName}</h3>
                                <p className="text-[10px] text-gray-500 font-medium">
                                    Page {currentStep} of {totalPages}
                                </p>
                            </div>
                        </div>

                        {/* Pagination Dots */}
                        <div className="flex gap-2">
                            {activeProfessions.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentStep(index + 1)}
                                    className={`h-2 rounded-full transition-all duration-300 ${currentStep === index + 1 ? 'bg-blue-600 w-6' : 'bg-gray-200 w-2 hover:bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Document Upload Fields */}
                    <div className="space-y-3 min-h-[300px]">
                        {currentProfession?.documents.map((doc) => {
                            const isUploaded = uploadedDocs[doc.id];
                            const DocIcon = doc.icon;

                            return (
                                <div
                                    key={doc.id}
                                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isUploaded
                                            ? 'border-green-200 bg-green-50'
                                            : 'border-gray-100 hover:border-gray-200 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                                            {isUploaded ? (
                                                <Check className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <DocIcon className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 text-sm">{doc.label}</p>
                                            {isUploaded && (
                                                <p className="text-xs text-green-600">{uploadedDocs[doc.id].name}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isUploaded ? (
                                            <button
                                                onClick={() => removeDocument(doc.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => triggerFileUpload(doc.id)}
                                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                            >
                                                <Upload className="w-4 h-4" />
                                                Upload
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Custom Documents for current profession */}
                        {(customDocuments[currentProfessionName] || []).map((doc) => {
                            const isUploaded = uploadedDocs[doc.id];

                            return (
                                <div
                                    key={doc.id}
                                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isUploaded
                                            ? 'border-green-200 bg-green-50'
                                            : 'border-dashed border-gray-200 bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                                            {isUploaded ? (
                                                <Check className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <FileText className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800 text-sm">{doc.label}</p>
                                            <span className="text-[10px] text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">Custom</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isUploaded ? (
                                            <button
                                                onClick={() => removeDocument(doc.id)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => triggerFileUpload(doc.id)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                                >
                                                    <Upload className="w-4 h-4" />
                                                    Upload
                                                </button>
                                                <button
                                                    onClick={() => removeCustomDocument(doc.id)}
                                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add Custom Document */}
                        {showAddCustom ? (
                            <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50">
                                <input
                                    type="text"
                                    value={newCustomDocName}
                                    onChange={(e) => setNewCustomDocName(e.target.value)}
                                    placeholder="Enter document name"
                                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomDocument();
                                        if (e.key === 'Escape') setShowAddCustom(false);
                                    }}
                                />
                                <button onClick={addCustomDocument} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setShowAddCustom(false); setNewCustomDocName(''); }} className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowAddCustom(true)}
                                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-all"
                            >
                                <Plus className="w-5 h-5" />
                                <span className="font-medium">Add Custom Document</span>
                            </button>
                        )}
                    </div>

                    {/* Navigation Buttons - Like Signup Pages */}
                    <div className="mt-6 pt-4 border-t border-gray-50 flex justify-between gap-4">
                        {currentStep > 1 ? (
                            <button
                                onClick={() => setCurrentStep(prev => prev - 1)}
                                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-100 text-gray-600 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-[0.98] text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/docs')}
                                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-100 text-gray-600 font-bold hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-[0.98] text-sm"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Back
                            </button>
                        )}

                        {currentStep < totalPages ? (
                            <button
                                onClick={() => setCurrentStep(prev => prev + 1)}
                                className="flex-[1.5] flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] text-sm"
                            >
                                Continue
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <button
                                onClick={handleSave}
                                className="flex-[1.5] bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-2xl font-bold hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-600/20 active:scale-[0.98] text-sm flex items-center justify-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Save All Documents
                            </button>
                        )}
                    </div>
                </div>

                {/* Security Footer */}
                <p className="mt-4 text-center text-[10px] text-gray-400 font-medium">
                    Your data is secure and encrypted. Need help? <span className="text-blue-500 cursor-pointer hover:underline">Contact Support</span>
                </p>
            </div>
        </div>
    );
};

export default ProfessionDocuments;
